import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import EditCaseModal from "./EditCaseModal";
import { message, Modal } from "antd";
import { casesService } from "../../services/api.service";
import AddCaseModal from "./AddCaseModal";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select } from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import logger from "../../utils/logger";

export default function CaseList({
  cases = [],
  onAddNew,
  onViewDetails,
  onEdit,
  onRefresh,
  initialClientData = null,
}) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [showAddCaseModal, setShowAddCaseModal] = useState(false);
  const [editingCase, setEditingCase] = useState(null);

  const statusColors = {
    نشط: "success",
    "قيد النظر": "info",
    معلق: "warning",
    مغلق: "secondary",
  };

  const filteredCases = cases
    .filter((case_) => {
      const searchFields = [
        case_?.case_number,
        case_?.title,
        case_?.client_name,
        case_?.opponent_name,
      ]
        .filter(Boolean)
        .map((field) => field.toString().toLowerCase());

      const matchesSearch =
        searchTerm === "" ||
        searchFields.some((field) => field.includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === "all" || case_?.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.filing_date) - new Date(a.filing_date);
        case "oldest":
          return new Date(a.filing_date) - new Date(b.filing_date);
        case "priority":
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        default:
          return 0;
      }
    });

  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const currentCases = filteredCases.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleEdit = (caseData) => {
    setEditingCase(caseData);
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      const formData = {
        ...values,
        filing_date: values.filing_date,
        next_session_date: values.next_session_date,
      };

      const response = await casesService.updateCase(editingCase.id, formData);

      if (response.success) {
        message.success("تم تحديث القضية بنجاح");
        setIsEditModalVisible(false);

        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      logger.error("Error updating case:", error);
      message.error("فشل في تحديث القضية");
    }
  };

  const handleDelete = async (caseId, e) => {
    e.stopPropagation();
    Modal.confirm({
      title: "تأكيد الحذف",
      content: "هل أنت متأكد من حذف هذه القضية؟",
      okText: "حذف",
      cancelText: "إلغاء",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await casesService.deleteCase(caseId);
          message.success("تم حذف القضية بنجاح");
          if (onRefresh) {
            onRefresh();
          }
        } catch (error) {
          message.error("فشل في حذف القضية");
          logger.error("Delete error:", error);
        }
      },
    });
  };

  const handleCaseCreationSuccess = (data) => {
    // AddCaseModal الآن يتعامل مع إنشاء القضية بنفسه
    // نحن فقط نحتاج لتحديث القائمة وإغلاق modal
    setShowAddCaseModal(false);
    if (onRefresh) {
      onRefresh();
    }
    // لا حاجة لرسالة نجاح هنا لأن AddCaseModal يتعامل مع ذلك
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl">إدارة القضايا</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                عدد القضايا: {filteredCases.length}
              </p>
            </div>
            <Button onClick={() => setShowAddCaseModal(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة قضية جديدة
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="col-span-2 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="البحث في القضايا..."
                className="pr-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">جميع الحالات</option>
              <option value="نشط">نشط</option>
              <option value="قيد النظر">قيد النظر</option>
              <option value="معلق">معلق</option>
              <option value="مغلق">مغلق</option>
            </Select>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="newest">الأحدث</option>
              <option value="oldest">الأقدم</option>
              <option value="priority">الأولوية</option>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>رقم القضية</TableHead>
                  <TableHead>عنوان القضية</TableHead>
                  <TableHead>العميل</TableHead>
                  <TableHead>الخصم</TableHead>
                  <TableHead>نوع القضية</TableHead>
                  <TableHead>المحكمة</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="7" className="h-40">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <FileText className="h-12 w-12 mb-4 opacity-50" />
                        <p>لا توجد قضايا مطابقة لمعايير البحث</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentCases.map((case_) => (
                    <TableRow
                      key={case_?.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/cases/${case_?.id}`)}
                    >
                      <TableCell className="font-medium">
                        {case_?.case_number}
                      </TableCell>
                      <TableCell>{case_?.title}</TableCell>
                      <TableCell>{case_?.client_name}</TableCell>
                      <TableCell>{case_?.opponent_name}</TableCell>
                      <TableCell>{case_?.case_type}</TableCell>
                      <TableCell>{case_?.court_name}</TableCell>
                      <TableCell>
                        <Badge
                          variant={statusColors[case_?.status] || "secondary"}
                        >
                          {case_?.status || "غير محدد"}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/cases/${case_?.id}`)}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleEdit(case_, e)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDelete(case_?.id, e)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">
              عرض {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, filteredCases.length)} من{" "}
              {filteredCases.length} قضية
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {getPageNumbers().map((number) => (
                <Button
                  key={number}
                  variant={currentPage === number ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(number)}
                >
                  {number}
                </Button>
              ))}

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showAddCaseModal && (
        <AddCaseModal
          visible={showAddCaseModal}
          onClose={() => setShowAddCaseModal(false)}
          initialData={initialClientData}
          onSuccess={handleCaseCreationSuccess}
        />
      )}

      {isEditModalVisible && (
        <EditCaseModal
          visible={isEditModalVisible}
          onCancel={() => setIsEditModalVisible(false)}
          onSubmit={handleEditSubmit}
          initialData={editingCase}
        />
      )}
    </motion.div>
  );
}
